import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Optional camera-based scanning.
 *
 * Chromium implements BarcodeDetector natively. WebKit never has — and on iOS
 * every browser is WebKit underneath, so Chrome or Firefox on an iPad are no
 * different. Since the panel *is* an iPad, a WASM decoder (zxing, via the
 * barcode-detector ponyfill) is loaded on demand there. It is ~1 MB, so it is
 * only fetched when someone actually taps the camera on, and never on
 * Chromium.
 *
 * Still secondary to the HID scanner on purpose: no permission, no secure
 * context, and a wall-mounted panel's camera faces the room, not the pack.
 */
declare global {
  interface Window {
    BarcodeDetector?: {
      new (options?: { formats?: string[] }): {
        detect: (source: CanvasImageSource) => Promise<Array<{ rawValue: string }>>;
      };
      getSupportedFormats: () => Promise<string[]>;
    };
  }
}

/**
 * Formats worth looking for on groceries. Requesting a format the browser does
 * not implement makes the constructor throw NotSupportedError and kills the
 * whole scanner, so this list is always intersected with what it supports.
 *
 * UPC-A is deliberately absent: it is a subset of EAN-13 (same digits with a
 * leading zero) and several Chromium builds do not list it.
 */
const WANTED_FORMATS = ['ean_13', 'ean_8', 'upc_e', 'code_128', 'code_39', 'itf'];

type DetectorLike = { detect: (source: CanvasImageSource) => Promise<Array<{ rawValue: string }>> };

/**
 * Camera scanning now works anywhere getUserMedia does, because the decoder is
 * shipped rather than assumed. What it still needs is a secure context.
 */
export const cameraScanSupported = (): boolean =>
  typeof navigator !== 'undefined' && typeof navigator.mediaDevices?.getUserMedia === 'function';

const nativeSupported = () =>
  typeof window !== 'undefined' && typeof window.BarcodeDetector === 'function';

/** Resolves a detector, falling back to the WASM ponyfill on WebKit. */
async function loadDetector(formats: string[]): Promise<{ detector: DetectorLike; native: boolean }> {
  if (nativeSupported()) {
    const supported = await window.BarcodeDetector!.getSupportedFormats();
    const usable = formats.filter((f) => supported.includes(f));
    if (usable.length > 0) {
      return { detector: new window.BarcodeDetector!({ formats: usable }), native: true };
    }
  }

  const [{ BarcodeDetector: Ponyfill, setZXingModuleOverrides }, { default: wasmUrl }] = await Promise.all([
    import('barcode-detector/ponyfill'),
    import('zxing-wasm/reader/zxing_reader.wasm?url'),
  ]);
  // Serve the wasm from our own origin: the default is a CDN, which would be
  // an external dependency the panel cannot reach offline.
  setZXingModuleOverrides({
    locateFile: (path: string, prefix: string) => (path.endsWith('.wasm') ? wasmUrl : prefix + path),
  });
  return { detector: new Ponyfill({ formats: formats as never }) as DetectorLike, native: false };
}

export type CameraState = 'off' | 'starting' | 'live' | 'error';

export interface CameraChoice {
  deviceId: string;
  label: string;
}

/**
 * A wall-mounted panel is set up once and then left alone, so the chosen
 * camera has to survive reloads — the back camera usually faces the wall.
 */
const CAMERA_KEY = 'frisk.cameraId';

const rememberCamera = (id: string | null) => {
  try {
    if (id) localStorage.setItem(CAMERA_KEY, id);
    else localStorage.removeItem(CAMERA_KEY);
  } catch {
    /* private mode */
  }
};

const recalledCamera = () => {
  try {
    return localStorage.getItem(CAMERA_KEY);
  } catch {
    return null;
  }
};

/** Labels are empty until permission is granted, so fall back to position. */
/**
 * Which rear lens this is.
 *
 * A modern iPhone reports three or four rear cameras and every one of them
 * says "Back". Collapsing them all to "Hinten" made the switch button a blind
 * guess between identical entries, which matters because the lenses are not
 * interchangeable for this job.
 */
function lensOf(label: string): 'ultra' | 'tele' | 'main' | 'virtual' | null {
  if (/ultra|wide\s*angle/i.test(label) && /ultra/i.test(label)) return 'ultra';
  if (/tele/i.test(label)) return 'tele';
  if (/dual|triple/i.test(label)) return 'virtual';
  if (/back|rear|environment/i.test(label)) return 'main';
  return null;
}

function describeCamera(device: MediaDeviceInfo, index: number): string {
  const raw = device.label.trim();
  if (raw) {
    if (/front|user|face/i.test(raw)) return 'Vorne';
    switch (lensOf(raw)) {
      case 'ultra':
        return 'Hinten · Ultraweit';
      case 'tele':
        return 'Hinten · Tele';
      case 'virtual':
        return 'Hinten · Auto';
      case 'main':
        return 'Hinten';
    }
    return raw.replace(/\s*camera\s*/i, '').trim() || `Kamera ${index + 1}`;
  }
  return `Kamera ${index + 1}`;
}

/**
 * The lens to scan with, when the browser has left the choice to us.
 *
 * facingMode:'environment' lets iOS pick, and on a multi-lens iPhone it often
 * lands on Ultra Wide — which has the worst minimum focus distance of the set
 * and spreads a small barcode across far fewer pixels. Both are exactly wrong
 * for reading a code held at arm's length. The plain wide lens is the one that
 * behaves like the fixed-focus webcam this already works on.
 */
function preferredRearCamera(devices: MediaDeviceInfo[]): string | null {
  const rear = devices.filter((d) => d.kind === 'videoinput' && !/front|user|face/i.test(d.label));
  const main = rear.find((d) => lensOf(d.label) === 'main');
  if (main) return main.deviceId;
  const virtual = rear.find((d) => lensOf(d.label) === 'virtual');
  return virtual?.deviceId ?? null;
}

export function useCameraScanner(onDetect: (barcode: string) => void) {
  const [state, setState] = useState<CameraState>('off');
  const [error, setError] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [cameras, setCameras] = useState<CameraChoice[]>([]);
  const [activeCamera, setActiveCamera] = useState<string | null>(null);
  // Surfaced in the UI so "nothing is happening" is distinguishable from
  // "scanning hard and finding nothing".
  const [frames, setFrames] = useState(0);
  const pausedRef = useRef(false);
  const lastValueRef = useRef('');
  const lastAtRef = useRef(0);
  const [resolution, setResolution] = useState<string | null>(null);
  /**
   * What the decoder is actually being handed.
   *
   * "It does not recognise anything" has several very different causes — no
   * frames, a black canvas, a region too small to hold a barcode, or a decoder
   * throwing — and they are indistinguishable from the outside. These make the
   * difference visible instead of guessable.
   */
  const [roiSize, setRoiSize] = useState<string | null>(null);
  /** Mean brightness 0-255 of the decoded region; ~0 means nothing is arriving. */
  const [luma, setLuma] = useState<number | null>(null);
  const [lastError, setLastError] = useState<string | null>(null);
  /**
   * Zoom is the single biggest lever on a stubborn barcode: too few pixels per
   * bar and no amount of decoding effort invents them. Torch is second, since
   * a pack held under a kitchen cabinet is in its own shadow.
   *
   * Both are optional capabilities — Safari exposes neither on every device —
   * so the controls only appear where they actually work.
   */
  const [zoom, setZoomState] = useState(1);
  const [zoomRange, setZoomRange] = useState<{ min: number; max: number; step: number } | null>(null);
  const [torch, setTorchState] = useState(false);
  const [torchAvailable, setTorchAvailable] = useState(false);
  const rafRef = useRef<number>(undefined);
  const handler = useRef(onDetect);
  handler.current = onDetect;

  const stop = useCallback(() => {
    if (rafRef.current !== undefined) clearTimeout(rafRef.current);
    rafRef.current = undefined;
    // Stopping the track kills the torch with it, but the flag has to follow
    // or the button would come back lit on the next open.
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    setTorchState(false);
    setState('off');
  }, []);

  const fail = useCallback((message: string) => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    setState('error');
    setError(message);
  }, []);

  const start = useCallback(async (requestedId?: string) => {
    /*
     * Starting means decoding.
     *
     * pause() and resume() were added so the camera survives between items
     * rather than being torn down and re-acquired, which was slow and flaky on
     * iOS. But only resume() cleared the flag, and the phone scanner pauses
     * before it ever starts: its first phase is 'claiming', which is not
     * 'scanning', so the effect pauses a camera that does not exist yet. The
     * subsequent start() then brought up a live picture whose decode loop
     * skipped every frame — no attempts, no error, nothing to see.
     */
    pausedRef.current = false;

    if (!cameraScanSupported()) {
      fail('Dieser Browser kann keine Barcodes über die Kamera lesen');
      return;
    }
    // getUserMedia is unavailable outside a secure context, which is what you
    // hit when the panel loads the server over http://<LAN-IP> instead of https.
    if (!window.isSecureContext) {
      fail('Kamera braucht HTTPS — über eine http://-Adresse verweigert der Browser den Zugriff');
      return;
    }

    setState('starting');
    setError(null);
    setFrames(0);
    setResolution(null);

    let detector: DetectorLike;
    try {
      ({ detector } = await loadDetector(WANTED_FORMATS));
    } catch (err) {
      fail('Barcode-Decoder konnte nicht geladen werden');
      return;
    }

    try {
      // Prefer the camera this panel was set up with; fall back to the rear one
      // on a first run. An unknown stored id (devices can be renumbered) just
      // falls through to facingMode rather than failing.
      const wanted = requestedId ?? recalledCamera();
      const constraints: MediaStreamConstraints = {
        // Default capture is often 640x480, which is far too few pixels for a
        // barcode held at arm's length — the bars alias into mush. Ask for
        // 1080p and let the browser fall back if the sensor cannot.
        video: {
          ...(wanted ? { deviceId: { exact: wanted } } : { facingMode: 'environment' }),
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
      };

      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia(constraints);
      } catch (err) {
        if (!wanted) throw err;
        rememberCamera(null);
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } },
        });
      }
      streamRef.current = stream;

      // Labels only become readable once permission is granted, so the list is
      // built here rather than up front.
      let settingsId = stream.getVideoTracks()[0]?.getSettings().deviceId ?? null;
      try {
        const all = await navigator.mediaDevices.enumerateDevices();

        /*
         * Now that the labels are readable, check we were handed a sensible
         * lens. Only when nothing was explicitly asked for — an explicit pick,
         * stored or from the switch button, is the user's call and stands.
         */
        if (!wanted) {
          const preferred = preferredRearCamera(all);
          if (preferred && preferred !== settingsId) {
            try {
              const better = await navigator.mediaDevices.getUserMedia({
                video: {
                  deviceId: { exact: preferred },
                  width: { ideal: 1920 },
                  height: { ideal: 1080 },
                },
              });
              stream.getTracks().forEach((tr) => tr.stop());
              stream = better;
              streamRef.current = better;
              settingsId = better.getVideoTracks()[0]?.getSettings().deviceId ?? preferred;
            } catch {
              /* keep the stream we already have */
            }
          }
        }

        setCameras(
          all
            .filter((d) => d.kind === 'videoinput')
            .map((d, i) => ({ deviceId: d.deviceId, label: describeCamera(d, i) })),
        );
      } catch {
        setCameras([]);
      }
      setActiveCamera(settingsId);
      if (settingsId) rememberCamera(settingsId);

      // Derived after the lens swap: the earlier track may have been stopped.
      const track = stream.getVideoTracks()[0];

      // Close-range barcodes need the lens to actually focus. Not every
      // platform exposes this (Safari does not), so failure is ignored.
      try {
        await track?.applyConstraints({
          advanced: [{ focusMode: 'continuous' } as unknown as MediaTrackConstraintSet],
        });
      } catch {
        /* unsupported */
      }

      const video = videoRef.current;
      if (!video) throw new Error('no video element');
      video.srcObject = stream;
      await video.play();

      // detect() on a video with no dimensions yet returns nothing, forever.
      // play() can resolve before metadata has arrived on iOS, so wait for the
      // frame size explicitly rather than assuming.
      if (!video.videoWidth) {
        await new Promise<void>((resolve) => {
          const done = () => resolve();
          video.addEventListener('loadedmetadata', done, { once: true });
          setTimeout(done, 3000);
        });
      }
      if (!video.videoWidth) {
        fail('Kamerabild hat keine Größe — Seite neu laden');
        return;
      }
      setResolution(`${video.videoWidth}×${video.videoHeight}`);

      // Capabilities are only meaningful once the track is live.
      type Caps = MediaTrackCapabilities & {
        zoom?: { min: number; max: number; step?: number };
        torch?: boolean;
      };
      const caps = (track?.getCapabilities?.() ?? {}) as Caps;
      if (caps.zoom && caps.zoom.max > caps.zoom.min) {
        setZoomRange({ min: caps.zoom.min, max: caps.zoom.max, step: caps.zoom.step || 0.1 });
        const current = (track?.getSettings?.() as { zoom?: number } | undefined)?.zoom;
        setZoomState(current ?? caps.zoom.min);
      } else {
        setZoomRange(null);
      }
      setTorchAvailable(Boolean(caps.torch));
      setTorchState(false);

      let consecutiveErrors = 0;

      /**
       * Decode the guide box, not the whole frame.
       *
       * zxing's own options are already maxed out by default (tryHarder,
       * tryRotate, tryInvert), so the remaining win is giving it less to chew
       * on: a 1080p frame is ~2 megapixels of mostly kitchen, and cropping to
       * the band where the pack is actually held cuts the work per attempt —
       * which buys more attempts per second, and fewer distractions in frame.
       */
      const roi = document.createElement('canvas');
      const roiCtx = roi.getContext('2d', { willReadFrequently: true });
      const CROP_W = 0.9;
      const CROP_H = 0.55;

      /*
       * Brightness is sampled through a 32x32 downscale rather than
       * getImageData on the full region: reading a megapixel every frame would
       * cost more than the decode it is meant to diagnose.
       */
      const probe = document.createElement('canvas');
      probe.width = 32;
      probe.height = 32;
      const probeCtx = probe.getContext('2d', { willReadFrequently: true });
      let sampleCountdown = 0;

      const sampleBrightness = () => {
        if (!probeCtx || roi.width === 0) return;
        probeCtx.drawImage(roi, 0, 0, 32, 32);
        const { data } = probeCtx.getImageData(0, 0, 32, 32);
        let total = 0;
        for (let i = 0; i < data.length; i += 4) {
          total += (data[i]! * 299 + data[i + 1]! * 587 + data[i + 2]! * 114) / 1000;
        }
        setLuma(Math.round(total / (data.length / 4)));
      };

      const frameSource = (): CanvasImageSource => {
        if (!roiCtx) return video;
        const sw = Math.round(video.videoWidth * CROP_W);
        const sh = Math.round(video.videoHeight * CROP_H);
        if (sw === 0 || sh === 0) return video;
        if (roi.width !== sw || roi.height !== sh) {
          roi.width = sw;
          roi.height = sh;
        }
        roiCtx.drawImage(
          video,
          Math.round((video.videoWidth - sw) / 2),
          Math.round((video.videoHeight - sh) / 2),
          sw, sh, 0, 0, sw, sh,
        );
        if (sampleCountdown-- <= 0) {
          sampleCountdown = 12; // about once a second
          setRoiSize(`${sw}×${sh}`);
          sampleBrightness();
        }
        return roi;
      };

      const tick = async () => {
        if (!streamRef.current) return;
        // Paused keeps the stream open but stops decoding: re-acquiring the
        // camera between items is slow and flaky on iOS.
        if (pausedRef.current) {
          rafRef.current = window.setTimeout(() => void tick(), 200);
          return;
        }
        try {
          const found = await detector.detect(frameSource());
          consecutiveErrors = 0;
          setFrames((n) => n + 1);
          const value = found[0]?.rawValue;
          const now = performance.now();
          // The same pack stays in frame for many frames — and is still there
          // after you finish entering it, so this has to outlive a pause.
          if (value && (value !== lastValueRef.current || now - lastAtRef.current > 4000)) {
            lastValueRef.current = value;
            lastAtRef.current = now;
            handler.current(value);
          }
        } catch (err) {
          // One bad frame is noise; a steady stream of them is a broken
          // decoder, and silently retrying forever is what made this look
          // like "nothing happens".
          consecutiveErrors++;
          // Surfaced even below the give-up threshold: an intermittent decoder
          // error explains a scanner that reads nothing without ever failing.
          setLastError(err instanceof Error ? err.message : 'unbekannt');
          if (consecutiveErrors >= 10) {
            fail(`Dekoder-Fehler: ${err instanceof Error ? err.message : 'unbekannt'}`);
            return;
          }
        }
        // A cropped decode measures ~10 ms, so ~12 attempts/sec leaves the UI
        // thread free while giving a wobbling hand plenty of chances.
        rafRef.current = window.setTimeout(() => void tick(), 80);
      };

      setState('live');
      rafRef.current = window.setTimeout(() => void tick(), 0);
    } catch (err) {
      const name = err instanceof DOMException ? err.name : '';
      fail(
        name === 'NotAllowedError'
          ? 'Kamera-Zugriff verweigert — im Browser für diese Seite erlauben'
          : name === 'NotFoundError'
            ? 'Keine Kamera gefunden'
            : name === 'NotReadableError'
              ? 'Kamera wird von einer anderen App benutzt'
              : 'Kamera konnte nicht gestartet werden',
      );
    }
  }, [fail]);

  /** Stop decoding without giving the camera back. */
  /** Optical or digital zoom, whichever the device offers. */
  const setZoom = useCallback(async (value: number) => {
    const track = streamRef.current?.getVideoTracks()[0];
    if (!track) return;
    try {
      await track.applyConstraints({ advanced: [{ zoom: value } as unknown as MediaTrackConstraintSet] });
      setZoomState(value);
    } catch {
      /* device refused the value — leave the previous one showing */
    }
  }, []);

  const toggleTorch = useCallback(async () => {
    const track = streamRef.current?.getVideoTracks()[0];
    if (!track) return;
    const next = !torch;
    try {
      await track.applyConstraints({ advanced: [{ torch: next } as unknown as MediaTrackConstraintSet] });
      setTorchState(next);
    } catch {
      /* unsupported after all */
    }
  }, [torch]);

  const pause = useCallback(() => {
    pausedRef.current = true;
  }, []);

  const resume = useCallback(() => {
    // Re-arm against whatever is currently in frame, so finishing one item
    // does not instantly re-open the same one.
    lastAtRef.current = performance.now();
    pausedRef.current = false;
  }, []);

  /** Switch to a specific camera, or cycle to the next one. */
  const switchCamera = useCallback(
    async (deviceId?: string) => {
      const list = cameras;
      let next = deviceId;
      if (!next) {
        if (list.length < 2) return;
        const current = list.findIndex((c) => c.deviceId === activeCamera);
        next = list[(current + 1) % list.length]!.deviceId;
      }
      // Release the current device first: iOS will not hand out a second
      // camera while the first is still held.
      stop();
      await start(next);
    },
    [cameras, activeCamera, stop, start],
  );

  useEffect(() => stop, [stop]);

  return {
    state, error, videoRef, start, stop, pause, resume,
    cameras, activeCamera, switchCamera, frames, resolution, roiSize, luma, lastError,
    zoom, zoomRange, setZoom, torch, torchAvailable, toggleTorch,
  };
}
